import { useCallback, useRef } from "react";

const SWIPE_THRESHOLD = 0.3;

// A touch that starts inside something with its own horizontal gesture — a
// sideways-scrolling row (filter chips, shelves) or a drag handle that
// claims the touch (touch-action: none) — belongs to that element, not to
// the swipe.
const ownsGesture = (target: EventTarget, root: Element): boolean => {
  for (
    let el = target instanceof Element ? target : null;
    el && el !== root;
    el = el.parentElement
  ) {
    const style = window.getComputedStyle(el);
    if (style.touchAction === "none") return true;
    if (/auto|scroll/.test(style.overflowX) && el.scrollWidth > el.clientWidth)
      return true;
  }
  return false;
};

export function useSwipeToDismiss<T extends HTMLElement = HTMLDivElement>(
  onDismiss: () => void,
) {
  const ref = useRef<T>(null);
  const touchRef = useRef<{
    startX: number;
    startY: number;
    locked: boolean;
    swiping: boolean;
  } | null>(null);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (ownsGesture(e.target, e.currentTarget)) {
      touchRef.current = null;
      return;
    }
    const t = e.touches[0];
    touchRef.current = {
      startX: t.clientX,
      startY: t.clientY,
      locked: false,
      swiping: false,
    };
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    const state = touchRef.current;
    const el = ref.current;
    if (!state || !el) return;

    const t = e.touches[0];
    const dx = t.clientX - state.startX;
    const dy = t.clientY - state.startY;

    if (!state.locked) {
      if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
      state.locked = true;
      state.swiping = Math.abs(dx) > Math.abs(dy);
      if (!state.swiping) return;
    }

    if (!state.swiping) return;

    const offset = Math.max(0, dx);
    el.style.transform = `translateX(${offset}px)`;
    el.style.transition = "none";
  }, []);

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const state = touchRef.current;
      const el = ref.current;
      touchRef.current = null;
      if (!state?.swiping || !el) return;

      const dx = e.changedTouches[0].clientX - state.startX;
      const threshold = window.innerWidth * SWIPE_THRESHOLD;

      if (dx > threshold) {
        el.style.transition = "transform 0.2s ease-out";
        el.style.transform = "translateX(100%)";
        setTimeout(onDismiss, 200);
      } else {
        el.style.transition = "transform 0.2s ease-out";
        el.style.transform = "";
      }
    },
    [onDismiss],
  );

  // Clears the drag offset. Call after navigating when the swiped element
  // stays mounted (e.g. the same page component re-renders for the previous
  // route), otherwise it would stay parked off-screen.
  const reset = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.transition = "";
    el.style.transform = "";
  }, []);

  return { ref, onTouchStart, onTouchMove, onTouchEnd, reset };
}
